import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Payment,
  AccountBalance,
  AttachMoney,
  Home,
  Person,
  Business,
  CheckCircle,
  Receipt,
} from '@mui/icons-material';

const PLCpayment = () => {
  const canWritePLC = hasWritePermission('plot-booking.plc-payment');
  const [searchForm, setSearchForm] = useState({
    plotNoBookingId: '',
    site: '',
    sector: '',
    block: '',
  });

  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    balanceAmount: '',
    paymentMode: '',
    manualReceiptNumber: '',
    paymentAmount: '',
    paymentDate: '',
    receivedBy: '',
    givenBy: '',
    cashDepositeBy: '',
    narration: '',
  });

  // Sample data for PLC payment
  const [paymentData] = useState({
    customerId: 'CUST001',
    customerName: 'John Smith',
    associateId: 'ASSOC001',
    plotStatus: 'Booked',
    paymentPlan: 'Installment',
    plotRate: '₹4,500/sq ft',
    plotNo: 'P101',
    plotSize: '30x40 ft',
    bookingId: 'BK001',
    bookingDate: '2025-01-15',
    allotmentDate: '2025-01-20',
    paidAmount: '₹5,40,000',
    plcPercentage: '15%',
    plotAmount: '₹54,00,000',
    balanceAmount: '₹48,60,000',
    allotmentAmount: '₹10,80,000',
    balanceAllotmentAmount: '₹5,40,000',
    plcAmount: '₹8,10,000',
    siteName: 'Green Valley',
    sectorName: 'Sector A',
    blockName: 'Block 1',
    plotArea: '1200 sq ft',
    mobileNo: '9876543210',
    email: 'john.smith@email.com',
  });

  // Dropdown options
  const siteOptions = ['Green Valley', 'Hill View', 'Paradise Heights', 'Royal Gardens'];
  const sectorOptions = ['Sector A', 'Sector B', 'Sector C', 'Sector D'];
  const blockOptions = ['Block 1', 'Block 2', 'Block 3', 'Block 4'];
  const paymentModeOptions = ['Cash', 'Cheque', 'Online Transfer', 'UPI', 'Card', 'DD'];

  const handleSearchInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handlePaymentFormChange = (field, value) => {
    setPaymentForm({
      ...paymentForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSearch = async () => {
    if (!searchForm.plotNoBookingId.trim()) {
      setErrors({ plotNoBookingId: 'Please enter Plot No or Booking ID' });
      return;
    }

    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowPaymentDetails(true);
      setSuccessMessage('');
      // Set balance amount from payment data
      setPaymentForm({
        ...paymentForm,
        balanceAmount: paymentData.balanceAmount,
      });
      console.log('Searching with criteria:', searchForm);
      setSearchLoading(false);
    }, 1500);
  };

  const handleOpenPaymentDialog = () => {
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setErrors({});
  };

  const validatePaymentForm = () => {
    const newErrors = {};

    if (!paymentForm.paymentAmount.trim()) {
      newErrors.paymentAmount = 'Payment amount is required';
    }

    if (!paymentForm.paymentMode.trim()) {
      newErrors.paymentMode = 'Payment mode is required';
    }

    if (!paymentForm.paymentDate.trim()) {
      newErrors.paymentDate = 'Payment date is required';
    }

    return newErrors;
  };

  const handleSubmitPayment = async () => {
    const validationErrors = validatePaymentForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`PLC Payment of ${paymentForm.paymentAmount} has been submitted successfully for ${paymentData.bookingId}`);
      setPaymentDialogOpen(false);
      setShowPaymentDetails(false);
      setSearchForm({
        plotNoBookingId: '',
        site: '',
        sector: '',
        block: '',
      });
      // Reset payment form
      setPaymentForm({
        balanceAmount: paymentData.balanceAmount,
        paymentMode: '',
        manualReceiptNumber: '',
        paymentAmount: '',
        paymentDate: '',
        receivedBy: '',
        givenBy: '',
        cashDepositeBy: '',
        narration: '',
      });
      setErrors({});
      console.log('Payment submitted:', paymentForm);
      setSubmitLoading(false);
    }, 2000);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          PLC Payment Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Process and manage Property Legal Charges (PLC) payments for plot bookings
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Plot/Booking Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Plot No/Booking ID"
                variant="outlined"
                fullWidth
                value={searchForm.plotNoBookingId}
                onChange={(e) => handleSearchInputChange('plotNoBookingId', e.target.value)}
                placeholder="Plot No or Booking ID"
                error={!!errors.plotNoBookingId}
                helperText={errors.plotNoBookingId}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Site</InputLabel>
                <Select
                  value={searchForm.site}
                  label="Site"
                  onChange={(e) => handleSearchInputChange('site', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Sites</MenuItem>
                  {siteOptions.map((site) => (
                    <MenuItem key={site} value={site}>
                      {site}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sector</InputLabel>
                <Select
                  value={searchForm.sector}
                  label="Sector"
                  onChange={(e) => handleSearchInputChange('sector', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Sectors</MenuItem>
                  {sectorOptions.map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      {sector}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Block</InputLabel>
                <Select
                  value={searchForm.block}
                  label="Block"
                  onChange={(e) => handleSearchInputChange('block', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Blocks</MenuItem>
                  {blockOptions.map((block) => (
                    <MenuItem key={block} value={block}>
                      {block}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={searchLoading || !searchForm.plotNoBookingId.trim()}
                  startIcon={<Search />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    borderRadius: 2
                  }}
                >
                  {searchLoading ? 'Searching...' : 'Search Plot/Booking'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Details */}
      {showPaymentDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <AttachMoney sx={{ color: '#f57c00', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                PLC Payment Details
              </Typography>
            </Box>

            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Plot/Booking Details for PLC Payment Processing
            </Typography>

            {/* Customer Information */}
            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Customer Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer ID</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {paymentData.customerId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentData.customerName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Associate ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentData.associateId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Status</Typography>
                    <Chip
                      label={paymentData.plotStatus}
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Plot Information */}
            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Plot Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {paymentData.plotNo}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Size</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentData.plotSize}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {paymentData.bookingId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Rate</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#f57c00' }}>
                      {paymentData.plotRate}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Financial Summary */}
            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Financial Summary
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Total Plot Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00' }}>
                      {paymentData.plotAmount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {paymentData.paidAmount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>
                      {paymentData.balanceAmount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">PLC Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {paymentData.plcAmount}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              {/* {canWritePLC && ( */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleOpenPaymentDialog}
                startIcon={<Payment />}
                sx={{
                  px: 6,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  borderRadius: 2
                }}
              >
                Process PLC Payment
              </Button>
              {/* )} */}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Payment sx={{ color: '#1976d2', fontSize: 28 }} />
            PLC Payment Form
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Balance Amount"
                variant="outlined"
                fullWidth
                value={paymentForm.balanceAmount}
                onChange={(e) => handlePaymentFormChange('balanceAmount', e.target.value)}
                disabled
                helperText="Current outstanding balance amount"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.paymentMode}>
                <InputLabel>Payment Mode *</InputLabel>
                <Select
                  value={paymentForm.paymentMode}
                  label="Payment Mode *"
                  onChange={(e) => handlePaymentFormChange('paymentMode', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {paymentModeOptions.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
                {errors.paymentMode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.paymentMode}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Manual Receipt Number"
                variant="outlined"
                fullWidth
                value={paymentForm.manualReceiptNumber}
                onChange={(e) => handlePaymentFormChange('manualReceiptNumber', e.target.value)}
                placeholder="Enter manual receipt number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Amount *"
                variant="outlined"
                fullWidth
                value={paymentForm.paymentAmount}
                onChange={(e) => handlePaymentFormChange('paymentAmount', e.target.value)}
                placeholder="Enter payment amount"
                error={!!errors.paymentAmount}
                helperText={errors.paymentAmount || "Enter the PLC payment amount"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Date *"
                type="date"
                variant="outlined"
                fullWidth
                value={paymentForm.paymentDate}
                onChange={(e) => handlePaymentFormChange('paymentDate', e.target.value)}
                error={!!errors.paymentDate}
                helperText={errors.paymentDate}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Received By"
                variant="outlined"
                fullWidth
                value={paymentForm.receivedBy}
                onChange={(e) => handlePaymentFormChange('receivedBy', e.target.value)}
                placeholder="Name of person who received payment"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Given By"
                variant="outlined"
                fullWidth
                value={paymentForm.givenBy}
                onChange={(e) => handlePaymentFormChange('givenBy', e.target.value)}
                placeholder="Name of person who gave payment"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Cash Deposited By"
                variant="outlined"
                fullWidth
                value={paymentForm.cashDepositeBy}
                onChange={(e) => handlePaymentFormChange('cashDepositeBy', e.target.value)}
                placeholder="Name of person who deposited cash"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBalance color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Narration"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={paymentForm.narration}
                onChange={(e) => handlePaymentFormChange('narration', e.target.value)}
                placeholder="Enter payment narration/remarks..."
                helperText="Additional remarks or notes about the payment"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          {/* {canWritePLC && ( */}
          <Button
            onClick={handleSubmitPayment}
            color="primary"
            variant="contained"
            disabled={!paymentForm.paymentAmount || !paymentForm.paymentMode || !paymentForm.paymentDate || submitLoading}
            startIcon={<Payment />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}
          >
            {submitLoading ? 'Processing...' : 'Submit Payment'}
          </Button>
          {/* )} */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PLCpayment;